// ============================================================================
// JARVISTRAVEL - SET UP TWO-FACTOR (JAR-1700)
// The production Clerk instance requires a second factor, so a new account's
// session is created PENDING with a `setup-mfa` task. `useAuth` treats that
// session as signed out; `useUser`, which this screen and App's routing both
// read, still hands back the user (see sessionTask.ts). This screen finishes
// the task.
//
// The QR is the fast path (JAR-1876, Brent): a scan takes the key in one go,
// where typing 32 characters is the slowest step in an otherwise effortless
// sign-in. The key stays under it for a desktop authenticator, or a camera that
// will not focus. This screen carried no QR until then, on the reasoning that a
// dependency was too much to add here; the trade is different now that this is
// the screen standing between a new customer and the product.
//
// JAR-2872: the authenticator app is no longer the only way through. A person
// without one (Charlie, the reason for this ticket) switches at the bottom to a
// texted code: the screen collects a phone number (E.164, the same rule core
// stores), Clerk sends the code, and once it is verified the number is reserved
// as the account's second factor. Those are two calls. Verifying alone leaves
// the number a contact: two-factor stays off, the session keeps its setup task,
// and the person is sent back here for ever (JAR-3084, which is what happened
// to everyone who took this path before the reserve was added). The number
// then lives on the Clerk user and in core's profile, which is what Settings
// shows. Progress through the phone path
// is noted per account like the TOTP key (KEY_SHOWN below), so a relaunch
// resumes the code step instead of silently starting over.
//
// JAR-3581 (Brent): the texted code is now the main path. The screen opens on
// the phone number, and "Set up with an authenticator app instead" makes the
// key only when it is picked.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import { ShieldCheck, Copy } from 'lucide-react';
import { Button, Input, TotpQrCode, toast } from '@/app/components/shared';
import { clerkErrorMessage } from '@/app/utils/clerkError';
import { formatPhone, toE164, NOT_A_PHONE } from '@/app/utils/phone';
import { updateProfile } from '@/app/api/auth';
import { RESEND_COOLDOWN_SECONDS } from './emailCode';

interface SetupMfaScreenProps {
  /** Called once the second factor is on and the codes have been seen. */
  onDone: () => void;
  /** Leave without enrolling. Every other screen sends a session with this task
   *  back here, so this is the only way out (JAR-1715). */
  onSignOut: () => void;
}

type Stage = 'starting' | 'enrolling' | 'codes';

// The phone-number resource this visit is verifying. A Clerk resource carries
// its own methods, so the attempt goes through the same object that prepared
// it; it is not rendered, so it lives in a ref rather than state.
type PendingPhone = {
  id: string;
  phoneNumber: string;
  verification?: { status?: string | null } | null;
  prepareVerification: () => Promise<unknown>;
  attemptVerification: (params: { strategy: 'phone_code'; code: string }) => Promise<unknown>;
  /** What makes a verified number the second factor (JAR-3084). */
  setReservedForSecondFactor: (params: { reserved: boolean }) => Promise<unknown>;
  destroy: () => Promise<void>;
};

const RESERVE_FAILED = 'Your number is confirmed, but two-factor could not be turned on. Try again.';

// A key Clerk issues stays the one it checks until it's verified or replaced,
// but it can't be read back. So this screen notes, per account, that it showed
// one, and a return visit asks for that key's code instead of replacing it. An
// iPhone often relaunches a Home Screen app when the person comes back from
// their authenticator, so replacing the key on every visit voided it every time
// (JAR-2491). The jt. prefix means signing out clears it.
//
// The note lists every account shown a key here, space-separated, so two
// accounts enrolling on one device keep theirs. A lone id, the earlier form,
// reads as a list of one.
const KEY_SHOWN = 'jt.mfa.key-shown';
const keyShownTo = (): string[] => {
  try {
    return (localStorage.getItem(KEY_SHOWN) ?? '').split(' ').filter(Boolean);
  } catch {
    return [];
  }
};
/** False when storage refuses the note, so a return visit can't be recognised. */
const noteKeyShown = (userId: string): boolean => {
  try {
    const ids = keyShownTo();
    if (!ids.includes(userId)) localStorage.setItem(KEY_SHOWN, [...ids, userId].join(' '));
    return true;
  } catch {
    return false;
  }
};
const forgetKeyShown = (userId: string) => {
  try {
    const rest = keyShownTo().filter((id) => id !== userId);
    if (rest.length) localStorage.setItem(KEY_SHOWN, rest.join(' '));
    else localStorage.removeItem(KEY_SHOWN);
  } catch {
    // Nothing was stored.
  }
};

// Same contract for the phone path (JAR-2872): the code was texted to a number
// this screen registered, and a relaunch mid-verification must find THAT
// number's verification instead of collecting a number again. The note stores
// the phone-number id — not the user id, and not "any unverified number": an
// account can carry an unrelated stale unverified number, and resuming onto it
// would text and verify the wrong phone (AI first-pass, web-app#859).
const PHONE_TASK = 'jt.mfa.phone-task';
// Stored as `<userId>:<phoneNumberId>`: a shared browser can hold two accounts,
// and a relaunch under the second must find nothing rather than resume the
// first account's number (web-app#859 round 2).
const phoneTaskFor = (userId: string): string | null => {
  try {
    const note = localStorage.getItem(PHONE_TASK);
    if (!note?.startsWith(`${userId}:`)) return null;
    return note.slice(userId.length + 1) || null;
  } catch {
    return null;
  }
};
const notePhoneTask = (userId: string, phoneNumberId: string) => {
  try {
    localStorage.setItem(PHONE_TASK, `${userId}:${phoneNumberId}`);
  } catch {
    // Unwritable storage: a return visit collects the number again.
  }
};
const forgetPhoneTask = () => {
  try {
    localStorage.removeItem(PHONE_TASK);
  } catch {
    // Nothing was stored.
  }
};

// Clerk hands back the E.164 form; on screen the person gets the country code
// and the last four, which is enough to know which phone to pick up.
const maskPhone = (stored: string): string => (stored.length > 4 ? `${stored.slice(0, 2)}***${stored.slice(-4)}` : stored);

export function SetupMfaScreen({ onDone, onSignOut }: SetupMfaScreenProps) {
  const { isLoaded, user } = useUser();
  const [stage, setStage] = useState<Stage>('starting');
  const [secret, setSecret] = useState('');
  // The otpauth:// URI Clerk mints with the key: what the QR encodes.
  const [uri, setUri] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupCodesFailed, setBackupCodesFailed] = useState(false);
  // This visit found a key it had already shown this person.
  const [cameBack, setCameBack] = useState(false);
  // Storage refused the note, so coming back will mean a new key.
  const [unremembered, setUnremembered] = useState(false);
  const started = useRef(false);
  // The texted-code path (JAR-2872), where the screen opens (JAR-3581, Brent).
  // phoneEntry shows the number field in place of the QR block; everything else
  // is the phone attempt in flight.
  const [phoneEntry, setPhoneEntry] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneInvalid, setPhoneInvalid] = useState(false);
  const [phoneHint, setPhoneHint] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSent, setPhoneSent] = useState<'sending' | 'sent' | 'failed'>('sending');
  const [resendIn, setResendIn] = useState(0);
  const phoneRef = useRef<PendingPhone | null>(null);
  const [hasPendingPhone, setHasPendingPhone] = useState(false);
  // ONE writer for both (JAR-2203 review): the ref backs the async handlers and the
  // state is what the UI renders from, so setting them separately is how they drift.
  const setPendingPhone = (pn: PendingPhone | null) => {
    phoneRef.current = pn;
    setHasPendingPhone(pn !== null);
  };
  // The pending number's code was accepted. Clerk refuses a second attempt on a
  // verified number, so a reserve that failed is tried again without one.
  const phoneVerified = useRef(false);
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const start = useCallback(async () => {
    if (!user) return;
    setBusy(true);
    setError('');
    try {
      const totp = await user.createTOTP();
      // No key is nothing to enrol with, so this is not a started setup.
      if (!totp?.secret) {
        setError('Two-factor setup could not be started. Try again.');
        return;
      }
      setSecret(totp.secret);
      setUri(totp.uri ?? '');
      setStage('enrolling');
      setUnremembered(!noteKeyShown(user.id));
    } catch (err) {
      setError(clerkErrorMessage(err, 'Two-factor setup could not be started. Try again.'));
    } finally {
      setBusy(false);
    }
  }, [user]);

  /** The backup codes both paths end with: fetched once, shown once. The
   *  caller hands over the number IT verified — null on the TOTP path, the
   *  E.164 on the phone path, including the resume path (reading it from
   *  phoneRef missed the resume, whose resource comes from Clerk's user
   *  object; AI first-pass, web-app#859). */
  const issueBackupCodes = useCallback(async (enrolledPhone?: string) => {
    if (!user) return;
    // The task is done as far as this screen is concerned; any per-attempt
    // notes can go, whether this attempt was TOTP, the phone, or a resume.
    forgetKeyShown(user.id);
    forgetPhoneTask();
    // The number this screen verified is saved to core's profile, which is
    // what Settings' edit-profile form shows (JAR-2872, Brent: "it will save
    // the phone in the user record"). Fire-and-forget: enrolment is already
    // complete on Clerk's side, and a failed save must not stand between the
    // person and their backup codes. It is logged, so "Settings can correct
    // it by hand" starts from something actionable; the Clerk number remains
    // the second factor either way.
    if (enrolledPhone) {
      void updateProfile({ phone: enrolledPhone }).catch((err) => {
        console.error('[SetupMfaScreen] profile phone save failed', err);
      });
    }
    try {
      const bc = await user.createBackupCode();
      setBackupCodes(bc?.codes ?? []);
      setBackupCodesFailed(!bc?.codes?.length);
    } catch {
      setBackupCodes([]);
      setBackupCodesFailed(true);
    }
    setStage('codes');
  }, [user]);

  /** Register the typed number and have Clerk text it (JAR-2872). The resend
   *  cooldown carries across number changes: each cycle texts a real phone,
   *  and without this gate, change-number churn could send at network cadence
   *  to numbers the person never verifies (security review, web-app#859). */
  const startPhone = async () => {
    if (!user || busy || resendIn > 0) return;
    const e164 = toE164(phoneInput);
    if (!e164) {
      setPhoneInvalid(true);
      return;
    }
    setBusy(true);
    setError('');
    // A number this account already holds is not registered twice: Clerk
    // refuses it. One whose code was accepted before only needs reserving,
    // which is how someone whose two-factor never turned on gets through
    // (JAR-3084). One that was never verified is texted again.
    const held = (user.phoneNumbers ?? []).find((p) => p.phoneNumber === e164) as unknown as PendingPhone | undefined;
    if (held?.verification?.status === 'verified') {
      try {
        await held.setReservedForSecondFactor({ reserved: true });
      } catch (err) {
        setError(clerkErrorMessage(err, RESERVE_FAILED));
        setBusy(false);
        return;
      }
      try {
        await issueBackupCodes(held.phoneNumber);
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const pn = held ?? ((await user.createPhoneNumber({ phoneNumber: e164 })) as unknown as PendingPhone);
      if (!pn?.id) {
        setError('Two-factor setup could not be started. Try again.');
        return;
      }
      try {
        await pn.prepareVerification();
      } catch (prepareErr) {
        // Nothing was texted, so a number registered just now earned its keep
        // nowhere: drop it rather than leave an orphan unverified entry
        // against Clerk's per-user cap (caught in review).
        if (!held) void pn.destroy().catch(() => {});
        throw prepareErr;
      }
      setPendingPhone(pn);
      phoneVerified.current = false;
      // The TOTP key this visit showed is abandoned: the person chose the phone.
      // Clearing it here (not at the switch) keeps the QR intact while they are
      // only typing a number, and it comes back through the "Get a new key"
      // flow if they come back — which warns that the key was replaced.
      setSecret('');
      setUri('');
      setPhoneHint(maskPhone(pn.phoneNumber));
      setPhoneSent('sent');
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setStage('enrolling');
      setPhoneEntry(false);
      notePhoneTask(user.id, pn.id);
    } catch (err) {
      setError(clerkErrorMessage(err, 'We could not text that number. Check it and try again.'));
    } finally {
      setBusy(false);
    }
  };

  /** Ask Clerk to text the code again, on the same number. */
  const resendPhone = async () => {
    if (!phoneRef.current || busy || resendIn > 0) return;
    setBusy(true);
    setError('');
    setPhoneSent('sending');
    try {
      await phoneRef.current.prepareVerification();
      setPhoneSent('sent');
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setPhoneSent('failed');
      setError(clerkErrorMessage(err, 'We could not text that number. Use Resend to try again.'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    if (!user) return;
    setBusy(true);
    setError('');
    // ONLY this call decides whether enrolment failed. Everything after it runs
    // with the factor already on, so none of it may report the code as wrong.
    try {
      await user.verifyTOTP({ code: trimmed });
    } catch (err) {
      // A return visit checks a key this screen no longer shows. If Clerk has
      // dropped it, every code fails alike, so point at the way out. A code that
      // never reached Clerk says nothing about the key, and nor does a rate
      // limit, so neither does.
      setError(
        cameBack && !secret && isClerkAPIResponseError(err) && err.status !== 429
          ? "That code wasn't accepted. If it keeps happening, get a new key and add that one instead."
          : clerkErrorMessage(
              err,
              "That code wasn't accepted. Check your app and try again.",
              "Couldn't verify that code. Please try again.",
            ),
      );
      setBusy(false);
      return;
    }
    try {
      await issueBackupCodes();
    } finally {
      setBusy(false);
    }
  };

  /** Verify the texted code and turn two-factor on (JAR-2872). */
  const confirmPhone = async () => {
    const pn = phoneRef.current;
    const trimmed = phoneCode.trim();
    if (!pn) {
      setError('Two-factor setup could not be started. Try again.');
      return;
    }
    if (!trimmed) {
      setError('Enter the 6-digit code we texted you');
      return;
    }
    setBusy(true);
    setError('');
    // ONLY this call decides whether enrolment failed. Same rule as the
    // authenticator path above — and the same reading of the answer: ONLY an
    // explicitly verified result is success. Anything else (a rejection, an
    // undefined answer, a status that is not 'verified') is a rejected code,
    // never a pass on missing data (web-app#859 round 2).
    if (!phoneVerified.current) {
      try {
        const result = (await pn.attemptVerification({ strategy: 'phone_code', code: trimmed })) as
          | { verification?: { status?: string | null } }
          | undefined;
        if (result?.verification?.status !== 'verified') {
          setError("That code wasn't accepted. Check your texts and try again.");
          setBusy(false);
          return;
        }
      } catch (err) {
        setError(clerkErrorMessage(err, "That code wasn't accepted. Check your texts and try again."));
        setBusy(false);
        return;
      }
      phoneVerified.current = true;
    }
    // The code proved the phone. This is what turns two-factor on: until the
    // number is reserved it is a contact, and Clerk still wants a second factor.
    try {
      await pn.setReservedForSecondFactor({ reserved: true });
    } catch (err) {
      setError(clerkErrorMessage(err, RESERVE_FAILED));
      setBusy(false);
      return;
    }
    try {
      await issueBackupCodes(pn.phoneNumber);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${what} copied`);
    } catch {
      toast.error(`${what} could not be copied. Select it and copy by hand.`);
    }
  };

  // Once, when Clerk has a user: React 18 mounts twice in development, and the
  // routing below must answer only at the start (see the latch note).
  useEffect(() => {
    if (!isLoaded || !user || started.current) return;
    started.current = true;
    // Someone who ALREADY has a second factor has nothing to enrol, and this
    // path is the one App deliberately does not route a signed-in user off —
    // so a bookmark, a typed URL or a restored tab would otherwise leave them
    // here while createTOTP either fails on a screen with no way out, or mints
    // a fresh secret that can replace the authenticator they rely on (#440
    // gate, Fury). TwoFactorSettings reads the same field (:135). JAR-2872 adds
    // twoFactorEnabled, which a verified phone factor also turns on.
    //
    // AFTER the latch, and that order is the whole point. verifyTOTP below is
    // what turns totpEnabled on, and Clerk's `user` is a new object on the
    // re-render that follows — so the effect runs again mid-enrolment. Checked
    // before the latch, this would call onDone the moment the factor turned
    // on and unmount the screen before the backup codes were shown, which is
    // the round-2 bug in a new place. Here it can only ever answer at the start.
    if (user.totpEnabled || user.twoFactorEnabled) {
      forgetKeyShown(user.id);
      forgetPhoneTask();
      onDone();
      return;
    }
    // A phone verification left unfinished by a relaunch resumes at its code
    // step: the noted number is read back from Clerk's user object, which
    // survives the reload with the session. Only THIS account's noted number
    // qualifies — any other unverified number belongs to some other story.
    const pendingPhoneId = phoneTaskFor(user.id);
    const pendingPhone = pendingPhoneId
      ? (user.phoneNumbers ?? []).find((p) => p.id === pendingPhoneId && p.verification?.status !== 'verified')
      : undefined;
    if (pendingPhoneId && pendingPhone) {
      setPendingPhone(pendingPhone as unknown as PendingPhone);
      setPhoneHint(maskPhone(pendingPhone.phoneNumber));
      setPhoneSent('sent');
      setStage('enrolling');
      setPhoneEntry(false);
      return;
    }
    if (pendingPhoneId && !pendingPhone) forgetPhoneTask();
    if (keyShownTo().includes(user.id)) {
      setCameBack(true);
      setPhoneEntry(false);
    }
    // Anyone else stays on the phone number. The authenticator's key is made
    // only when they pick that path (JAR-3581).
  }, [isLoaded, user, onDone]);

  // The texted-code states, read once: which form the enrolling stage shows and
  // what its button row says.
  // Derived from state, not read off phoneRef during render (JAR-2203). The ref
  // still holds the pending phone for the async handlers; this mirror is what the
  // UI reads, and it is set wherever the ref is.
  const phoneActive = hasPendingPhone && !secret;
  const phoneSentence =
    phoneSent === 'sending'
      ? 'Sending a code to your phone.'
      : phoneSent === 'sent'
        ? `We sent a 6-digit code to ${phoneHint || 'your phone'}. Enter it to finish setting up.`
        : `We couldn't send a code to ${phoneHint || 'your phone'}. Use Resend to try again.`;

  /** Drop the pending number and collect a different one. */
  const changePhone = async () => {
    const pn = phoneRef.current;
    setPhoneEntry(true);
    setPhoneInput('');
    setPhoneHint('');
    setPhoneCode('');
    setPhoneInvalid(false);
    setError('');
    setPendingPhone(null);
    phoneVerified.current = false;
    forgetPhoneTask();
    if (!pn) return;
    try {
      await pn.destroy();
    } catch {
      // Clerk keeps the unverified number; a re-enrolment replaces it.
    }
  };

  return (
    <div className="min-h-[100dvh] bg-bg flex flex-col">
      <div className="flex-1 px-6 py-10 max-w-md mx-auto w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand grid place-items-center mx-auto mb-4">
            <ShieldCheck className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <h1 className="text-2xl font-bold text-ink">Set up two-factor</h1>
          <p className="text-muted mt-1">
            {stage === 'codes'
              ? 'Save these backup codes before you continue.'
              : 'Your account needs a second factor before you can go on.'}
          </p>
        </div>

        {stage !== 'codes' && phoneEntry && (
          <div className="space-y-5">
            <div className="bg-surface-raised rounded-2xl p-4 space-y-3">
              <p className="text-sm text-body">Enter your phone number and we'll text you a 6-digit code.</p>
              <Input
                label="Phone number"
                value={phoneInput}
                onChange={(e) => {
                  setPhoneInput(formatPhone(e.target.value));
                  setPhoneInvalid(false);
                }}
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                error={phoneInvalid ? NOT_A_PHONE : undefined}
              />
              {error && <p className="text-sm text-danger">{error}</p>}
            </div>
            <Button
              onClick={() => void startPhone()}
              variant="primary"
              fullWidth
              size="lg"
              loading={busy}
              disabled={resendIn > 0}
            >
              {resendIn > 0 ? `Text me a code in ${resendIn}s` : 'Text me a code'}
            </Button>
            <button
              type="button"
              onClick={() => {
                setPhoneEntry(false);
                setError('');
                // The key is made the first time this path is picked (JAR-3581).
                if (!secret && !cameBack && !busy) void start();
              }}
              className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              Set up with an authenticator app instead
            </button>
            {/* The screen opens here now, so this step carries the way out
                (JAR-1715). */}
            <button
              type="button"
              onClick={onSignOut}
              className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              Sign out
            </button>
          </div>
        )}

        {stage !== 'codes' && !phoneEntry && phoneActive && (
          <div className="space-y-5">
            <div className="bg-surface-raised rounded-2xl p-4 space-y-3">
              <p role="status" className="text-sm text-body">
                {phoneSentence}
              </p>
              <Input
                label="6-digit code"
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
              />
              {error && <p className="text-sm text-danger">{error}</p>}
            </div>
            <Button
              onClick={() => void confirmPhone()}
              variant="primary"
              fullWidth
              size="lg"
              loading={busy}
              disabled={!phoneCode}
            >
              Turn on two-factor
            </Button>
            <button
              type="button"
              onClick={() => void resendPhone()}
              disabled={busy || resendIn > 0}
              className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              {phoneSent === 'sending'
                ? 'Sending code'
                : resendIn > 0
                  ? `Resend code in ${resendIn}s`
                  : 'Resend code'}
            </button>
            <button
              type="button"
              onClick={() => void changePhone()}
              disabled={busy}
              className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              Use a different phone number
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              Sign out
            </button>
          </div>
        )}

        {stage !== 'codes' && !phoneEntry && !phoneActive && (
          <div className="space-y-5">
            {cameBack && !secret ? (
              <div className="bg-surface-raised rounded-2xl p-4 space-y-3">
                <p className="text-sm text-body">
                  You were shown a key here before. If you added it to your authenticator app, enter
                  its 6-digit code below.
                </p>
                <Button variant="secondary" onClick={() => void start()} disabled={busy}>
                  Get a new key
                </Button>
              </div>
            ) : (
              <div className="bg-surface-raised rounded-2xl p-4 space-y-3">
                {cameBack ? (
                  <p role="status" className="text-sm text-body">
                    This key replaces the one you were shown before. If you added that one, remove it
                    from your authenticator app and add this one.
                  </p>
                ) : unremembered && (
                  <p role="status" className="text-sm text-body">
                    This browser can't keep your place here. If you come back and see a different key,
                    remove the old one from your authenticator app and add the new one.
                  </p>
                )}
                <p className="text-sm text-body">
                  {uri
                    ? 'Open your authenticator app and scan this. No camera? Use the key below.'
                    : 'Open your authenticator app, add an account by key, and paste this in.'}
                </p>
                {uri && (
                  <div className="flex justify-center">
                    <TotpQrCode uri={uri} />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono text-ink break-all bg-surface rounded-xl p-3">
                    {secret || '…'}
                  </code>
                  <Button
                    variant="secondary"
                    onClick={() => copy(secret, 'Key')}
                    disabled={!secret}
                    aria-label="Copy the setup key"
                  >
                    <Copy className="w-4 h-4" strokeWidth={1.75} />
                  </Button>
                </div>
              </div>
            )}

            <Input
              label="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
            />
            {error && <p className="text-sm text-danger">{error}</p>}
            {/* One button, two jobs. With no key there is nothing to confirm,
                so after a failed start it retries instead of sitting disabled
                with no way forward. A return visit confirms the key it showed
                before, which Clerk still holds. */}
            <Button
              onClick={secret || cameBack ? confirm : () => void start()}
              variant="primary"
              fullWidth
              size="lg"
              loading={busy}
              disabled={!secret && !cameBack && !error}
            >
              {secret || cameBack || !error ? 'Turn on two-factor' : 'Try again'}
            </Button>
            {/* Back to the texted code, the main path (JAR-2872, JAR-3581). Its
                first step collects the number; it saves to the user record
                either way. */}
            {!cameBack && (
              <button
                type="button"
                onClick={() => {
                  setPhoneEntry(true);
                  setError('');
                }}
                className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
              >
                Use a text message code instead
              </button>
            )}
            <button
              type="button"
              onClick={onSignOut}
              className="w-full py-2 text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              Sign out
            </button>
          </div>
        )}

        {stage === 'codes' && (
          <div className="space-y-5">
            {backupCodesFailed ? (
              <p className="text-sm text-body">
                Two-factor is on. Backup codes could not be created just now, so you can make
                them later in Settings.
              </p>
            ) : (
              <>
                <div className="bg-surface-raised rounded-2xl p-4">
                  <ul className="grid grid-cols-2 gap-2 font-mono text-sm text-ink">
                    {backupCodes.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-sm text-muted">
                  Each code works once, and this is the only time they are shown.
                </p>
                <Button variant="secondary" fullWidth onClick={() => copy(backupCodes.join('\n'), 'Backup codes')}>
                  <Copy className="w-4 h-4" strokeWidth={1.75} /> Copy codes
                </Button>
              </>
            )}
            <Button onClick={onDone} variant="primary" fullWidth size="lg">Continue</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SetupMfaScreen;
